import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Paper,
} from '@mui/material';
import useAPI from '../../hooks/useAPI';
import { useDarkMode } from '../../hooks/useDarkMode';
import OtpInput from '../../components/otp-input';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (response?: any) => void;
  isSwitching?: boolean;
  isAlreadySetUp?: boolean;
  enableCompanyMfaAfterSetup?: boolean;
  email?: string; // Optional email for login-time setup (no auth required)
}

const TotpSetupDialog: React.FC<Props> = ({ open, onClose, onSuccess, isSwitching = false, isAlreadySetUp = false, enableCompanyMfaAfterSetup = false, email }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const API = useAPI();
  const [loading, setLoading] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skipQrCode, setSkipQrCode] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  useEffect(() => {
    if (open) {
      if (isSwitching && isAlreadySetUp) {
        // When switching and already set up, skip QR code and go straight to verification
        setSkipQrCode(true);
        setLoading(false);
      } else {
        // New setup - generate QR code
        setSkipQrCode(false);
        setupTotp();
      }
    } else {
      setQrCodeDataUrl(null);
      setVerificationCode('');
      setError(null);
      setSkipQrCode(false);
      setVerificationAttempted(false);
    }
  }, [open, isSwitching, isAlreadySetUp]);

  const setupTotp = async () => {
    try {
      setLoading(true);
      setError(null);
      // If email is provided, use login-time endpoint (no auth required)
      const response = await API.setupTotp(email);
      setQrCodeDataUrl(response.qrCodeDataUrl);
    } catch (err: any) {
      console.error('[TOTP Setup] Error:', err);
      const errorMessage = err?.response?.data?.message || err?.message || t('sentences:pages.mfa_setup.totp_setup_error') || 'Failed to set up TOTP';
      setError(errorMessage);
      // If it's a 404 or route not found, provide more helpful error
      if (err?.response?.status === 404 || errorMessage.includes('Route not found')) {
        setError('Unable to set up TOTP. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (code?: string) => {
    // Get the code to verify - prefer the passed code, fallback to state
    let codeToVerify = code || verificationCode;
    
    // Ensure we have exactly 6 digits
    codeToVerify = codeToVerify.replace(/\D/g, '').slice(0, 6);
    if (!codeToVerify || codeToVerify.length !== 6) {
      console.log('[TOTP Verify] Invalid code length:', codeToVerify?.length, 'Code:', codeToVerify);
      return;
    }
    
    console.log('[TOTP Verify] Verifying code:', codeToVerify, 'Length:', codeToVerify.length);
    setVerificationAttempted(true);
    try {
      setVerifying(true);
      setError(null);
      
      // verifyTotp already handles switching (sets preferredMethod to TOTP)
      // If email is provided, use login-time endpoint (no auth required)
      const response = await API.verifyTotp(codeToVerify, email);
      
      // If this is login-time setup, the response includes accessToken and user
      // We should handle that in the parent component
      if (email && response.accessToken) {
        // Login-time setup completed - parent should handle the token
      }
      
      // If this is admin/owner setting up their method to enable company MFA, enable it now
      if (enableCompanyMfaAfterSetup) {
        try {
          await API.put(`companies/updateMfa`, { mfa: true });
        } catch (err: any) {
          console.error('[TOTP Setup] Error enabling company MFA:', err);
          setError(err?.response?.data?.message || 'Failed to enable company MFA');
          setVerifying(false);
          return;
        }
      }
      
      // Pass response to onSuccess callback (for login-time setup)
      onSuccess(response);
      onClose();
    } catch (err: any) {
      console.error('[TOTP Setup] Verification error:', err);
      setError(err?.response?.data?.message || t('sentences:pages.mfa_setup.totp_verify_error') || 'Invalid verification code');
      setVerificationCode('');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
        }
      }}
    >
      <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px' }}>
          {isSwitching 
            ? (t('sentences:pages.mfa_setup.totp_switch_title') || 'Switch to Authenticator App')
            : (t('sentences:pages.mfa_setup.totp_title') || 'Set Up 2FA with an Authenticator App')}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3 }}>
        {loading && !skipQrCode ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {error && verificationAttempted && (
              <Alert 
                severity="error" 
                sx={{ mb: 0 }} 
                onClose={() => {
                  setError(null);
                  setVerificationAttempted(false);
                }}
              >
                {error}
              </Alert>
            )}

            {!skipQrCode && qrCodeDataUrl && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary', 
                    textAlign: 'center',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    maxWidth: '400px'
                  }}
                >
                  {t('sentences:pages.mfa_setup.totp_description') || 
                    'Scan the QR code in your Authenticator App (Google Authenticator, Microsoft Authenticator, Authy, etc.)'}
                </Typography>
                
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    maxWidth: '280px'
                  }}
                >
                  <img 
                    src={qrCodeDataUrl} 
                    alt="QR Code" 
                    style={{ 
                      width: '280px',
                      height: '280px',
                      objectFit: 'contain'
                    }} 
                  />
                </Box>
              </Box>
            )}

            {isSwitching && isAlreadySetUp && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary', 
                  textAlign: 'center',
                  fontSize: '14px',
                  lineHeight: 1.5
                }}
              >
                {t('sentences:pages.mfa_setup.totp_switch_description') || 
                  'Please verify your authenticator app code to switch to this method'}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: '15px',
                  color: 'text.primary',
                  textAlign: 'center',
                  mb: 0.5
                }}
              >
                {t('sentences:pages.mfa_setup.totp_enter_code') || 'Enter the 6-digit code from your Authenticator app'}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', px: 2 }}>
                <OtpInput
                  length={6}
                  value={verificationCode}
                  onChange={(value) => {
                    setVerificationCode(value);
                    // Clear error and reset verification attempt when user types
                    if (error) {
                      setError(null);
                    }
                    setVerificationAttempted(false);
                  }}
                  disabled={verifying}
                  error={false}
                  onComplete={(value) => {
                    // Auto-submit when all 6 digits are entered
                    // Use the value directly to avoid state timing issues
                    if (value && value.length === 6) {
                      // Small delay to ensure state is updated, then verify with the value
                      setTimeout(() => {
                        handleVerify(value);
                      }, 100);
                    }
                    // Clear any previous errors
                    if (error) {
                      setError(null);
                    }
                    setVerificationAttempted(false);
                  }}
                  onEnter={() => {
                    // Allow Enter key to submit when all digits are filled
                    if (verificationCode.length === 6 && !verifying) {
                      handleVerify();
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
        <Button 
          onClick={onClose} 
          disabled={verifying}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            minWidth: '100px'
          }}
        >
          {t('verbs.cancel')}
        </Button>
        <Button
          onClick={() => handleVerify()}
          variant="contained"
          disabled={verifying || loading || verificationCode.length !== 6}
          sx={{
            backgroundColor: '#1976d2',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            minWidth: '120px',
            px: 3,
            '&:hover': {
              backgroundColor: '#1565c0',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e',
            }
          }}
        >
          {verifying ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1, color: 'white' }} />
              {t('verbs.verifying') || 'Verifying...'}
            </>
          ) : (
            t('verbs.verify')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TotpSetupDialog;

